import { useLayoutEffect, useRef, type CSSProperties, type PointerEvent, type ReactNode } from 'react';
import { assetUrl } from '../api/assets';
import type { CompanionDto } from '../api/companions';
import type { PetBaseVariant } from '../compositor/assetPaths';
import { Pet3DStage } from '../pet3d/Pet3DStage';
import type { Point } from './wanderMath';
import type { WanderFacing, WanderMotion } from './useHabitatWander';
import type { PokeBurst } from './pokeReactions';

// The live pet's species and stage (it mounts once the wardrobe read answers); what it WEARS is
// `companion.outfit`, the same field every still picture and the battle floor read (W5).
export type HabitatSceneWardrobe = {
  speciesCode: string;
  stage: string;
};

export function HabitatScene(props: {
  companion: CompanionDto;
  wardrobe: HabitatSceneWardrobe | null;
  /** Task 16 (2026-08-23 battle-gore-and-wash): the wash fade, forwarded to the 3D pet's stains. */
  stainOpacity?: number;
  baseVariant?: PetBaseVariant;
  pos: Point;
  facing: WanderFacing;
  motion: WanderMotion;
  dull?: boolean;
  /** Holds the live 3D pet still (the Dress overlay covers the room with its own live pet). */
  paused?: boolean;
  onPetActivate?: () => void;
  petDisabled?: boolean;
  pokeClass?: string | null;
  pokeBursts?: PokeBurst[];
  pokeGen?: number;
  top: ReactNode;
  floor: ReactNode;
  extras?: ReactNode;
  onPointerDown?: (e: PointerEvent<HTMLDivElement>) => void;
  onPointerMove?: (e: PointerEvent<HTMLDivElement>) => void;
  onPointerUp?: (e: PointerEvent<HTMLDivElement>) => void;
  onPointerCancel?: (e: PointerEvent<HTMLDivElement>) => void;
  className?: string;
}) {
  const {
    companion, wardrobe, stainOpacity, baseVariant, pos, facing, motion, dull, paused,
    onPetActivate, petDisabled, pokeClass, pokeBursts = [], pokeGen = 0, top, floor, extras,
    onPointerDown, onPointerMove, onPointerUp, onPointerCancel, className,
  } = props;
  const hold = baseVariant === 'eat' || baseVariant === 'joy' ? baseVariant : null;
  const actorClass = 'habitat-scene-actor';
  const wrapClass = [
    'habitat-scene-actor-wrap',
    facing === 'left' ? 'face-left' : undefined,
  ].filter(Boolean).join(' ');
  const motionClass = ['habitat-scene-actor-motion', motion, pokeClass ?? undefined]
    .filter(Boolean)
    .join(' ');
  // Restart the poke keyframes on every poke (the same class twice would not). This used to be
  // key={pokeGen} on the motion span, which would remount the live 3D scene on every tap.
  const motionRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = motionRef.current;
    if (!el || !pokeGen) return;
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
  }, [pokeGen]);

  return (
    <div
      className={['habitat-scene', className].filter(Boolean).join(' ')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <img
        className="habitat-scene-bg"
        src={assetUrl('bd_habitat_default')}
        alt=""
        width={1024}
        height={2216}
      />
      {/* Both stickers render only with content: on the desk the identity lives in the Studio's
          left panel and the dock in its Dashboard (Task 7), and an empty `.habitat-scene-top` /
          `.habitat-scene-floor` would still paint its padded card over the room. */}
      {top != null && <div className="habitat-scene-top">{top}</div>}
      <button
        type="button"
        className={actorClass}
        // onboard F12: the only control on the pet itself; a screen reader used to hear "button".
        aria-label={`${companion.name} — tap to pet`}
        style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
        disabled={petDisabled}
        onClick={onPetActivate}
      >
        <span className={wrapClass}>
          <span className={motionClass} ref={motionRef}>
            {wardrobe ? (
              <Pet3DStage
                species={wardrobe.speciesCode}
                stage={wardrobe.stage}
                variant={baseVariant ?? 'base'}
                alt={`${companion.name} the ${companion.speciesName}`}
                motion={motion}
                facing={facing}
                stains={companion.stains}
                outfit={companion.outfit}
                stainOpacity={stainOpacity ?? 1}
                pokeClass={pokeClass ?? null}
                pokeGen={pokeGen}
                paused={paused}
                className={[hold ?? undefined, dull ? 'compositor-dull' : undefined].filter(Boolean).join(' ') || undefined}
              />
            ) : (
              <div className="compositor skeleton" aria-hidden="true" />
            )}
          </span>
        </span>
        {pokeBursts.map((burst) => (
          <span
            key={`${pokeGen}-${burst.id}`}
            className={burst.kind === 'react' ? 'habitat-poke-react' : 'habitat-poke-burst'}
            aria-hidden="true"
            style={{
              '--rx': burst.rx,
              '--ry': burst.ry,
              animationDelay: burst.delay,
            } as CSSProperties}
          >
            {burst.glyph}
          </span>
        ))}
      </button>
      {extras}
      {floor != null && <div className="habitat-scene-floor">{floor}</div>}
    </div>
  );
}
